import { useState, useCallback } from 'react';
import { useJobCacheInvalidation } from '../../hooks/useJobCacheInvalidation';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Stack,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';

import { FilingType, useGetFirmAsUser, useCreateJobForFirmAsUser } from '@taxassist/api-client';
import { isValidUuid } from '../../utils/formatting';
import { FileUpload } from './FileUpload';
import type { FileUploadItem } from '../../hooks/useFileUpload';

interface CreateJobDialogProps {
  open: boolean;
  onClose: () => void;
  firmId: string;
  onJobCreated?: (jobId: string) => void;
}

// Only show filing types that SurePrep supports (1040 and 1041)
const FILING_TYPE_OPTIONS: { value: FilingType; label: string }[] = [
  { value: '1040', label: '1040 - Individual Income Tax Return' },
  { value: '1041', label: '1041 - Estate and Trust Income Tax Return' },
  // Note: 1065 (Partnership), 1120 (Corporation), and 1120S (S Corporation) 
  // are not supported by SurePrep and have been removed from job creation
];

const isProduction = process.env.NODE_ENV === 'production';
const steps = ['Job Details', 'Upload Files', 'Submit'];

export function CreateJobDialog({ open, onClose, firmId, onJobCreated }: CreateJobDialogProps) {
  const { invalidateJobRelatedQueries } = useJobCacheInvalidation();
  const [activeStep, setActiveStep] = useState(0);
  const [createdJobId, setCreatedJobId] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<FileUploadItem[]>([]);

  const taxYear = new Date().getFullYear() - 1

  const { data: firmData } = useGetFirmAsUser(firmId || "disabled", {
    query: {
      queryKey: ["firm", firmId],
      enabled: !!firmId,
    },
  });


  const [formData, setFormData] = useState({
    clientName: '',
    clientNumber: '',
    filingType: '1040' as FilingType,
    taxYear,
    engagementPid: '',
  });

  const createJobMutation = useCreateJobForFirmAsUser({
    mutation: {
      onSuccess: async (data) => {
        setCreatedJobId(data.jobId);
        setActiveStep(1); // Move to file upload step
      },
    },
  });

  const handleNext = () => {
    if (activeStep === 0) {
      // Create job and move to file upload
      createJobMutation.mutate({
        firmId,
        data: {
          firmId,
          clientName: formData.clientName,
          clientNumber: formData.clientNumber,
          filingType: formData.filingType,
          taxYear: formData.taxYear,
          ...(formData.engagementPid.trim() && { engagementPid: formData.engagementPid.trim() }),
        },
      });
    } else if (activeStep === 1) {
      // Move to submit step
      setActiveStep(2);
    } else if (activeStep === 2) {
      // Complete the flow
      handleComplete();
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleComplete = async () => {
    if (createdJobId) {
      // Invalidate job queries when completing the job creation flow
      // This handles cases where files weren't uploaded but job was created
      if (uploadedFiles.length === 0) {
        await invalidateJobRelatedQueries({
          firmId,
          jobId: createdJobId
        });
      }
      onJobCreated?.(createdJobId);
    }
    handleDialogClose();
  };

  const handleDialogClose = () => {
    onClose();
    // Reset form state
    setActiveStep(0);
    setCreatedJobId(null);
    setUploadedFiles([]);
    setFormData({
      clientName: '',
      clientNumber: '',
      filingType: '1040',
      taxYear: new Date().getFullYear() - 1,
      engagementPid: '',
    });
  };

  const handleChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === 'taxYear' ? parseInt(event.target.value) : event.target.value,
    }));
  };

  const handleUploadComplete = useCallback(async (files: FileUploadItem[]) => {
    setUploadedFiles(files);
    
    // Invalidate job queries after file upload (consolidated invalidation point)
    if (createdJobId) {
      await invalidateJobRelatedQueries({
        firmId,
        jobId: createdJobId
      });
    }
  }, [invalidateJobRelatedQueries, firmId, createdJobId]);

  const handleUploadError = useCallback((error: Error) => {
    console.error('Upload error:', error);
  }, []);

  const isStepCompleted = (step: number): boolean => {
    switch (step) {
      case 0:
        return !!createdJobId;
      case 1:
        return uploadedFiles.length > 0;
      case 2:
        return false; // Final step
      default:
        return false;
    }
  };

  const isNextDisabled = (): boolean => {
    if (activeStep === 0) {
      return !formData.clientName || !formData.clientNumber || createJobMutation.isPending;
    }
    return false;
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={2}>
            <TextField
              fullWidth
              required
              label="Client Name"
              value={formData.clientName}
              onChange={handleChange('clientName')}
              disabled={createJobMutation.isPending}
              autoFocus
            />
            
            <TextField
              fullWidth
              required
              label={`${firmData?.surePrepConfig.taxSoftwareName ?? 'Tax Software'} Client ID`}
              value={formData.clientNumber}
              onChange={handleChange('clientNumber')}
              disabled={createJobMutation.isPending}
              placeholder="e.g., CLT-2024-001"
            />
            
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                required
                select
                label="Filing Type"
                value={formData.filingType}
                onChange={handleChange('filingType')}
                disabled={createJobMutation.isPending}
              >
                {FILING_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              
              <TextField
                fullWidth
                required
                type="number"
                label="Tax Year"
                value={formData.taxYear}
                onChange={handleChange('taxYear')}
                disabled={createJobMutation.isPending}
                inputProps={{
                  min: 2020,
                  max: new Date().getFullYear(),
                }}
              />
            </Box>

            <TextField
              fullWidth
              label="Engagement ID"
              value={formData.engagementPid}
              onChange={handleChange('engagementPid')}
              disabled={createJobMutation.isPending}
              placeholder="Optional — UUID from Socrates"
              error={!!formData.engagementPid.trim() && !isValidUuid(formData.engagementPid.trim())}
              helperText={formData.engagementPid.trim() && !isValidUuid(formData.engagementPid.trim()) ? 'Must be a valid UUID' : 'Links this job to a Socrates engagement for status sync'}
            />
          </Stack>
        );
      case 1:
        return createdJobId ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Job created successfully! You can now upload documents for processing.
              Files are optional - you can skip this step and upload files later.
            </Typography>
            <FileUpload
              jobId={createdJobId}
              onUploadComplete={handleUploadComplete}
              onError={handleUploadError}
              maxFiles={20}
              maxFileSize={50 * 1024 * 1024} // 50MB
            />
          </Box>
        ) : (
          <CircularProgress />
        );
      case 2:
        return (
          <Box textAlign="center" py={3}>
            <Typography variant="h6" gutterBottom>
              Job Ready for Processing
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Job ID: {createdJobId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {uploadedFiles.length > 0 
                ? `${uploadedFiles.length} file(s) uploaded and ready for processing.`
                : 'No files uploaded. You can add files later from the job details page.'}
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        Create New Job
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Step {activeStep + 1} of {steps.length}: {steps[activeStep]}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {createJobMutation.error && (
          <Alert severity="error">
            Failed to create job. Please try again.
          </Alert>
        )}

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ pt: 2, pb: 2 }}>
          {steps.map((label, index) => (
            <Step key={label} completed={isStepCompleted(index)}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        <Box sx={{ flex: 1, pt: 1, overflow: 'auto' }}>
          {getStepContent(activeStep)}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={handleDialogClose}>
          Cancel
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={createJobMutation.isPending}>
            Back
          </Button>
        )}
        
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={isNextDisabled()}
            startIcon={createJobMutation.isPending && <CircularProgress size={20} />}
          >
            {activeStep === 0 && createJobMutation.isPending
              ? 'Creating...'
              : activeStep === 0
              ? 'Create Job'
              : 'Next'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleComplete}
            color="success"
          >
            Finish
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
